import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpenIcon, ChevronRightIcon, SearchIcon, FilterIcon, TagIcon, XIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { genreGlossaryData } from '../data/genreGlossaryData';
import { GenreGlossaryCard } from '../components/Genres/GenreGlossaryCard';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
const GenreGlossary = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  // Filter sections for the sidebar
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Main Genres',
    type: 'checkbox',
    options: genreGlossaryData.map(g => ({
      value: g.id,
      label: g.name
    })),
    value: selectedCategories,
    onChange: (val: string) => setSelectedCategories(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  const resetFilters = () => {
    setSelectedCategories([]);
    setSearchQuery('');
  };
  /**
   * Genres that survive the category + search filters. Subgenre-level filtering
   * happens inside each card so it can also pick the right tier tab. When the
   * GENRE itself matches the query we clear the card's query, so searching
   * "Romance" shows the whole genre rather than only same-named subgenres.
   */
  const filteredData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return genreGlossaryData.filter(genre => selectedCategories.length === 0 || selectedCategories.includes(genre.id)).map(genre => {
      const genreMatches = !query || genre.name.toLowerCase().includes(query) || genre.definition.toLowerCase().includes(query);
      const subgenreMatchCount = query ? genre.subgenres.filter(sub => sub.name.toLowerCase().includes(query) || sub.definition.toLowerCase().includes(query)).length : genre.subgenres.length;
      return {
        genre,
        // A genre-level hit shows the full genre; otherwise scope to matches.
        cardQuery: genreMatches ? '' : searchQuery,
        visible: genreMatches || subgenreMatchCount > 0,
        subgenreMatchCount
      };
    }).filter(row => row.visible);
  }, [searchQuery, selectedCategories]);
  const totalMatches = useMemo(() => filteredData.reduce((sum, row) => sum + row.subgenreMatchCount, 0), [filteredData]);
  return <div className={`min-h-screen ${dk.page}`} data-id="element-9905" data-mp="txj1kdb39gt1">
      {/* Header */}
      <div className={`py-6 px-4 ${dk.surface}`} data-id="element-9906" data-mp="tzvh6jtdjf2x">
        <div className="max-w-7xl mx-auto" data-id="element-9907" data-mp="pwz6dfa2sn9j">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-9908" data-mp="pya879jynky0">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-9909" data-mp="13wrv5521ktev">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-9910" data-mp="1cw52ns6lv7wy" />
            <Link to="/browse" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-9911" data-mp="1cutwxmtlnq37">
              Books
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-9912" data-mp="1mley9i0nr8pt" />
            <span className={dk.textPrimary} data-id="element-9913" data-mp="1hndaijsael80">Genres</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-9914" data-mp="1hpgde4ejg8u5">
            <div className="flex items-center gap-3" data-id="element-9915" data-mp="1wkw2dv09hejo">
              <BookOpenIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-9916" data-mp="1rmcdblqdfmn3" />
              <div data-id="element-9917" data-mp="3l49vxul1c8f7">
                <h1 className={`text-2xl md:text-3xl font-bold ${dk.textHeading}`} data-id="element-9918" data-mp="3l4thdl9w9exz">
                  Genre & Subgenre Glossary
                </h1>
                <p className={`mt-1 text-sm max-w-2xl ${dk.textSecondary}`} data-id="element-9919" data-mp="3uud0ms1iratd">
                  A reference guide to book genres, what defines them, and how
                  many books you'll find in each category.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-9920" data-mp="327ijo53wutkn">
              <div className="relative flex-grow md:w-80" data-id="element-9921" data-mp="375abkz4k2fou">
                <input type="search" placeholder="Search subgenres, e.g. Pitch Black..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} aria-label="Search genres and subgenres" className={`w-full px-5 py-2.5 pl-10 pr-10 rounded-lg border focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-9922" data-mp="3c3vcl0rmt3ec" />
                <SearchIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-9923" data-mp="2gtympxv596r4" />
                {searchQuery && <button onClick={() => setSearchQuery('')} aria-label="Clear search" className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${dk.textMuted} hover:${dk.textPrimary}`} data-id="element-9924" data-mp="273ds0lilcmdh">
                    <XIcon className="h-4 w-4" data-id="element-9925" data-mp="1t4nw397icsqp" />
                  </button>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky result summary — keeps the search reachable while scrolling */}
      {searchQuery && <div className={`sticky top-0 z-20 border-b ${dk.borderSubtle} ${dk.surface}`} data-id="element-9926" data-mp="3ijloq6bcd3h8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-4" data-id="element-9927" data-mp="3dl0q2bbqmruw">
            <p className={`text-sm ${dk.textSecondary}`} aria-live="polite" data-id="element-9928" data-mp="1bvbj6lu97xp2">
              <span className="font-semibold" data-id="element-9929" data-mp="1gm1ra6u2eeng">{totalMatches}</span>{' '}
              {totalMatches === 1 ? 'subgenre' : 'subgenres'} across{' '}
              <span className="font-semibold" data-id="element-9930" data-mp="11yytdz1ty3zl">{filteredData.length}</span>{' '}
              {filteredData.length === 1 ? 'genre' : 'genres'} matching “
              {searchQuery}”
            </p>
            <button onClick={() => setSearchQuery('')} className={`text-sm font-medium whitespace-nowrap ${rt.accentLink}`} data-id="element-9931" data-mp="1une2k4ya5s30">
              Clear
            </button>
          </div>
        </div>}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9932" data-mp="1pm6xwerfp90k">
        {/* Mobile Filter Button */}
        <div className="mb-6 lg:hidden" data-id="element-9933" data-mp="98gpivta7yl4">
          <button onClick={() => setMobileFiltersOpen(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border shadow-sm ${dk.card} ${dk.textSecondary}`} data-id="element-9934" data-mp="9auld6vsdetq">
            <FilterIcon className="h-4 w-4" data-id="element-9935" data-mp="2shovngzzqzrl" />
            Filters
            {selectedCategories.length > 0 && <span className={`ml-1 px-1.5 py-0.5 ${rt.badgeRadius} text-xs font-bold text-white ${rt.bgDark}`} data-id="element-9936" data-mp="31ga9wzyfjsmk">
                {selectedCategories.length}
              </span>}
          </button>
        </div>

        <div className="flex gap-8" data-id="element-9937" data-mp="29pw0bfxg92gp">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-9938" data-mp="24qjft1u1w7ha">
            <FilterSidebar sections={filterSections} onReset={resetFilters} data-id="element-9939" data-mp="2dolehaqbao1e" />
          </FilterSidebarWrapper>

          {/* Glossary Content */}
          <div className="flex-1 min-w-0 space-y-8" data-id="element-9940" data-mp="1m0bij0d18b90">
            {filteredData.length === 0 ? <div className={`text-center py-16 rounded-xl border border-dashed ${dk.border} ${dk.surface}`} data-id="element-9941" data-mp="2av2qqi0o0e3c">
                <TagIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-9942" data-mp="2ftnrcijtwzsb" />
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-9943" data-mp="2ju7r7tvsehi5">
                  No genres found
                </h3>
                <p className={`mb-4 ${dk.textSecondary}`} data-id="element-9944" data-mp="2orpo2lwj569s">
                  Try adjusting your search or filters.
                </p>
                <button onClick={resetFilters} className={`font-medium ${rt.accentLink}`} data-id="element-9945" data-mp="2yrq8yca1rug3">
                  Clear all filters
                </button>
              </div> : filteredData.map(row => <GenreGlossaryCard key={row.genre.id} genre={row.genre} searchQuery={row.cardQuery} data-id="element-9946" data-mp="3cjwqa1oxs7h4" />)}
          </div>
        </div>
      </div>
    </div>;
};
export default GenreGlossary;